import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Container,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getAdvertisingBanners } from "../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../services/indexMediaBaseUrl";
import logo from "../../../../assets/didalogo.png";

const AUTOPLAY_MS = 5000;

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getAdvertisingBanners();
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setBanners(list);
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const goTo = useCallback(
    (newIndex) => {
      if (!banners.length) return;
      setFade(false);
      setTimeout(() => {
        setIndex(((newIndex % banners.length) + banners.length) % banners.length);
        setFade(true);
      }, 300);
    },
    [banners.length]
  );

  const startAutoplay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (banners.length <= 1) return;
    timerRef.current = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % banners.length);
        setFade(true);
      }, 300);
    }, AUTOPLAY_MS);
  }, [banners.length]);

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(timerRef.current);
  }, [startAutoplay]);

  const prev = () => {
    clearInterval(timerRef.current);
    goTo(index - 1);
    startAutoplay();
  };

  const next = () => {
    clearInterval(timerRef.current);
    goTo(index + 1);
    startAutoplay();
  };

  const goToDot = (i) => {
    clearInterval(timerRef.current);
    goTo(i);
    startAutoplay();
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: { xs: 320, md: 520 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "grey.100",
        }}
      >
        <CircularProgress sx={{ color: "primary.main" }} size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="error" fontWeight={600}>
          {isAr ? "حدث خطأ في تحميل الإعلانات" : "Error loading banners"}
        </Typography>
      </Box>
    );
  }

  const current = banners.length > 0 ? banners[index] : null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 320, sm: 400, md: 520, lg: 580 },
        overflow: "hidden",
        bgcolor: "#0a0a0a",
      }}
    >
      {/* Background Image */}
      {current && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${getImageUrl(current.image_web || current.image_web)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: fade ? 1 : 0,
            transition: "opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.5) 100%)",
            },
          }}
        />
      )}

      {/* Fallback — no banners */}
      {!current && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 50%, #0a0a0a 100%)",
          }}
        />
      )}

      {/* Content Overlay */}
      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          pb: { xs: 5, md: 7 },
        }}
      >
        {/* Brand Badge — top-left */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: 16, md: 28 },
            left: { xs: 16, md: 32 },
            right: { xs: 16, md: 32 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(16px)",
              borderRadius: 3,
              px: 2.5,
              py: 1,
              border: "1px solid rgba(213, 170, 47, 0.3)",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="DIDA MAX"
              sx={{
                height: { xs: 28, md: 36 },
                filter: "brightness(1.2)",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "0.9rem", md: "1.1rem" },
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "0.05em",
              }}
            >
              DIDA MAX
            </Typography>
          </Box>

          {/* Slide Counter */}
          {banners.length > 1 && (
            <Box
              sx={{
                bgcolor: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(16px)",
                borderRadius: 2,
                px: 2,
                py: 0.5,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 700, fontSize: "0.75rem" }}
              >
                {index + 1} / {banners.length}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Banner Text Content */}
        <Box
          sx={{
            opacity: fade ? 1 : 0,
            transform: fade ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
            maxWidth: { xs: "100%", md: "65%" },
          }}
        >


          {!current && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box component="img" src={logo} alt="DIDA MAX" sx={{ height: { xs: 44, md: 60 }, filter: "brightness(1.2)" }} />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.8rem", md: "3rem" },
                    background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  DIDA MAX
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.7)", mb: 3, fontWeight: 500 }}>
                {isAr
                  ? "اكتشف أفضل المنتجات بأسعار مميزة وجودة عالية"
                  : "Discover top products with great deals & premium quality"}
              </Typography>
            </>
          )}

          {/* CTA Buttons */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              component={Link}
              to="/products"
              variant="contained"
              size="large"
              sx={{
                px: { xs: 3, md: 4 },
                py: 1.5,
                fontWeight: 800,
                fontSize: { xs: "0.85rem", md: "1rem" },
                borderRadius: 3,
                textTransform: "none",
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                boxShadow: "0 4px 20px rgba(213, 170, 47, 0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                  boxShadow: "0 6px 28px rgba(213, 170, 47, 0.5)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {isAr ? "تسوق الآن" : "Shop Now"}
            </Button>

            {current && (
              <Button
                component={Link}
                to={`/promotions/${current.id}`}
                variant="outlined"
                size="large"
                sx={{
                  px: { xs: 3, md: 4 },
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: { xs: "0.85rem", md: "1rem" },
                  borderRadius: 3,
                  textTransform: "none",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.4)",
                  borderWidth: 2,
                  backdropFilter: "blur(8px)",
                  bgcolor: "rgba(255,255,255,0.08)",
                  "&:hover": {
                    borderColor: "#d5aa2f",
                    bgcolor: "rgba(213, 170, 47, 0.15)",
                    borderWidth: 2,
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {isAr ? "عرض العروض" : "View Offers"}
              </Button>
            )}
          </Box>
        </Box>
      </Container>

      {/* Arrow Navigation */}
      {banners.length > 1 && (
        <>
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              left: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              width: { xs: 40, md: 52 },
              height: { xs: 40, md: 52 },
              bgcolor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(213, 170, 47, 0.4)",
                borderColor: "rgba(213, 170, 47, 0.6)",
                transform: "translateY(-50%) scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: { xs: 24, md: 30 } }} />
          </IconButton>

          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              right: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              width: { xs: 40, md: 52 },
              height: { xs: 40, md: 52 },
              bgcolor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(213, 170, 47, 0.4)",
                borderColor: "rgba(213, 170, 47, 0.6)",
                transform: "translateY(-50%) scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <ChevronRightIcon sx={{ fontSize: { xs: 24, md: 30 } }} />
          </IconButton>
        </>
      )}

      {/* Dot Indicators */}
      {banners.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 12, md: 20 },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            display: "flex",
            gap: 1,
            bgcolor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(12px)",
            borderRadius: 3,
            px: 2,
            py: 1,
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {banners.map((_, i) => (
            <Box
              key={i}
              onClick={() => goToDot(i)}
              sx={{
                width: i === index ? 28 : 10,
                height: 10,
                borderRadius: 5,
                bgcolor: i === index ? "primary.main" : "rgba(255,255,255,0.4)",
                cursor: "pointer",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: i === index ? "0 0 12px rgba(213, 170, 47, 0.6)" : "none",
                "&:hover": {
                  bgcolor: i === index ? "primary.main" : "rgba(255,255,255,0.7)",
                  transform: "scale(1.2)",
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Bottom Golden Line */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg, transparent, #d5aa2f, #e6c76a, #d5aa2f, transparent)",
          zIndex: 3,
        }}
      />

      {/* Progress Bar */}
      {banners.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 3,
            left: 0,
            height: 3,
            bgcolor: "primary.main",
            zIndex: 4,
            animation: `progress ${AUTOPLAY_MS}ms linear infinite`,
            "@keyframes progress": {
              from: { width: "0%" },
              to: { width: "100%" },
            },
            opacity: 0.7,
          }}
        />
      )}
    </Box>
  );
};

export default HeroSection;
